// v640.6: Pure helper for ability/spell tooltip math.
// Given a spell or ability + a character, returns a compact preview string
// like "1d4+1 + 0.5 × MEN(3) = 3–6 (avg 4)" so players can see damage/heal
// numbers before committing to a cast.
//
// IMPORTANT: This is a *preview* — it ignores combat-state-only multipliers
// (Third Eye, Magnify, Spin the Wheel, elemental weakness, level penalty)
// because those depend on the live battle. The base formula match the
// pre-multiplier core of CombatSystem.processSpellDamage / processHeal.

/**
 * Parse a dice string like "2d6+2" into {min,max,avg,roll}.
 * Returns null for invalid/missing input.
 */
export function parseDice(diceStr) {
  if (!diceStr || typeof diceStr !== 'string') return null;
  const m = diceStr.match(/^(\d+)d(\d+)(?:\s*([+-])\s*(\d+))?$/i);
  if (!m) return null;
  const count = parseInt(m[1], 10);
  const sides = parseInt(m[2], 10);
  const sign = m[3] === '-' ? -1 : 1;
  const flat = m[4] ? sign * parseInt(m[4], 10) : 0;
  const min = count * 1 + flat;
  const max = count * sides + flat;
  const avg = count * (sides + 1) / 2 + flat;
  return { min, max, avg: Math.round(avg * 10) / 10, roll: diceStr };
}

function menMod(character) {
  return Math.floor(((character?.mentality || 10) - 10) / 2);
}

/**
 * Compute spell damage/heal preview.
 * Mirrors the *pre-multiplier* portion of processSpellDamage:
 *   damage = roll(spell.dice) + scale × MEN_mod + flatPerLevel × level
 * Returns { min, max, avg, formula } or null if not previewable.
 */
export function getSpellPreview(spell, character, options = {}) {
  if (!spell || !spell.dice) return null;
  const dice = parseDice(spell.dice);
  if (!dice) return null;

  const mm = menMod(character);
  const scale = (typeof spell.scale === 'number') ? spell.scale : 0;
  const lvl = character?.level || 1;
  const flatLvl = (spell.flatPerLevel || 0) * lvl;
  const scaleBonus = Math.floor(scale * mm);
  const min = Math.max(1, dice.min + scaleBonus + flatLvl);
  const max = Math.max(1, dice.max + scaleBonus + flatLvl);
  const avg = Math.max(1, Math.round(dice.avg + scaleBonus + flatLvl));

  // Formula text: only include scale/flat parts that are non-zero
  const parts = [dice.roll];
  if (scale > 0) parts.push(`${scale} × MEN(${mm})`);
  if (flatLvl > 0) parts.push(`+${flatLvl} (lvl)`);
  const formula = parts.join(' + ');

  const isHeal = (spell.type === 'heal' || /heal/i.test(spell.type || ''));
  const verb = options.verbOverride || (isHeal ? 'heal' : 'dmg');

  return { min, max, avg, formula, verb, element: spell.element || null };
}

/**
 * Build a one-line tooltip string suitable for inline display.
 *   "🎯 1d4+1 + 0.5 × MEN(3) = 3–6 dmg (avg 4)"
 */
export function getSpellTooltip(spell, character) {
  const p = getSpellPreview(spell, character);
  if (!p) return null;
  const range = p.min === p.max ? `${p.min}` : `${p.min}–${p.max}`;
  return `${p.formula} = ${range} ${p.verb} (avg ${p.avg})`;
}

/**
 * Class ability scaling. The class abilities use a small set of formulas
 * defined in server/game/ClassAbilities.js; this helper covers the common
 * "X + STR_mod" damage strikes shown in tooltips. For abilities without a
 * preview-friendly formula, returns null and the UI should fall back to
 * the static description.
 */
export function getAbilityPreview(ability, character) {
  if (!ability || !ability.name) return null;
  const lvl = character?.level || 1;
  const strMod = Math.floor(((character?.strength || 10) - 10) / 2);
  const aglMod = Math.floor(((character?.agility || 10) - 10) / 2);
  const mm = menMod(character);

  // Heuristic: abilities frequently show "X damage" in their description.
  // We pull a few well-known scaling formulas from ClassAbilities.js.
  switch (ability.name) {
    case 'Precise Stab':
      return { text: `Auto-crit · 2 + STR(${strMod}) base damage` };
    case 'Critical Blow':
      return { text: `Auto-crit · 4 + STR(${strMod}) base damage` };
    case 'Recoil Strike':
      return { text: `3 + STR(${strMod}) damage; you take half back` };
    case 'Death Strike':
      return { text: `8 + STR(${strMod}) damage · 10% instant kill` };
    case 'Sneak Attack':
      return { text: `+${Math.max(0, aglMod * 2)} sneak damage (AGL × 2)` };
    case 'Critical Sneak Attack':
      return { text: `Auto-crit · ${Math.max(0, aglMod * 3)} sneak damage (AGL × 3)` };
    case 'Assassin Strike':
      return { text: `6 + AGL(${aglMod}) damage · 15% instant kill` };
    case 'Superior Assassin Strike':
      return { text: `6 + AGL(${aglMod}) damage · 40% instant kill` };
    case 'Power Cast':
      return { text: `Next spell deals 2× damage · MP cost waived (3 mana slots)` };
    case 'Mind Over Matter':
      return { text: `Convert ${Math.max(1, lvl)} HP → ${Math.max(1, lvl + Math.max(0, mm))} MP` };
    default:
      return null;
  }
}
